import type { BlocksLayoutType } from 'tabview/visualizations/aq-blocks/types';
import type { ModernizedBlockDiagramViewSettings } from 'tabview/blockDiagram/types';
import type { FlexibleBlockDiagramExportHandlers } from './exportHandlers';
import type { ArdoqId } from '@ardoq/api-types';

/**
 * View settings for the Flexible Block Diagram view. Deliberately a strict
 * superset of the (persisted) Block Diagram settings — the shared fields are
 * `Pick`ed straight from `ModernizedBlockDiagramViewSettings` (single source of
 * truth for their types) — plus the two FBD-only additions (`layoutType`,
 * `hideReferences`). This lets the feature flag be turned off with no conversion
 * (ARD-33247): Block Diagram reads these settings directly, ignoring the extra
 * fields, and Flexible Block Diagram reads Block Diagram settings directly, with
 * the additions defaulted by its own entry in `DEFAULT_VIEWPOINT_MODE_SETTINGS`
 * rather than by the readers here.
 */
export type FlexibleBlockDiagramViewSettings = Pick<
  ModernizedBlockDiagramViewSettings,
  | 'isLegendActive'
  | 'layoutOrientation'
  | 'separateReferences'
  | 'componentStyle'
  | 'activeDiffMode'
  | 'collapsedGroupIds'
> & {
  /**
   * `sanitizeSingleViewSettings` defaults only the keys stored settings omit, so a
   * stored `null` survives its `pick` and reaches the view. This value is forwarded to
   * aq-blocks raw, so the `??` in `toLayoutStyle` is what handles it.
   */
  layoutType: BlocksLayoutType | null;
  /** Omit reference lines entirely. FBD-only; Block Diagram ignores it. Takes
   * precedence over `separateReferences` when true. */
  hideReferences: boolean;
};

export type FlexibleBlockDiagramCommands = {
  onSelectionChange: (componentIds: ArdoqId[]) => void;
  onComponentDoubleClick: (componentId: ArdoqId) => void;
  onCollapsedGroupsChange: (collapsedGroupIds: string[]) => void;
  onReadOnlyChange: (isReadOnly: boolean) => void;
  closeContextMenu: VoidFunction;
};

/** The subset of the view model the toolbar (viewActionsOptions) reads. */
export type FlexibleBlockDiagramViewProperties = {
  viewSettings: FlexibleBlockDiagramViewSettings;
  /** Edit affordances are hidden in presentation mode. */
  isPresentationMode: boolean;
  isReadOnly: boolean;
  commands: FlexibleBlockDiagramCommands;
  /** Drives the disabled state of the expand/collapse-all toolbar buttons. */
  hasGroups: boolean;
  /** The empty state renders no diagram, so there is nothing to export. */
  isEmptyView: boolean;
  /** Backs the PNG and SVG entries of the view pane header's Save as menu. */
  exportHandlers: FlexibleBlockDiagramExportHandlers;
  /** Pairs with the view context's id; see the note in `viewModel$`. */
  viewInstanceId: string;
  /** Group id to display label, for the AI to reference groups by name. */
  groups: Record<string, string>;
};

const FLEXIBLE_BLOCK_DIAGRAM_VIEW_PROPERTIES_KEYS = [
  'viewSettings',
  'isPresentationMode',
  'isReadOnly',
  'commands',
  'hasGroups',
  'isEmptyView',
  'exportHandlers',
  'viewInstanceId',
  'groups',
] as const satisfies readonly (keyof FlexibleBlockDiagramViewProperties)[];

/**
 * The view-pane header reads these off an untyped `viewProperties` record, and
 * dereferences `exportHandlers`, so a mismatch would throw inside a reducer
 * rather than degrade to undefined.
 */
export const isFlexibleBlockDiagramViewProperties = (
  viewProperties: Record<string, unknown>
): viewProperties is FlexibleBlockDiagramViewProperties =>
  FLEXIBLE_BLOCK_DIAGRAM_VIEW_PROPERTIES_KEYS.every(
    key => viewProperties[key] !== undefined
  );
